import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { getApiUrl } from '../config/api';
import * as XLSX from 'xlsx';
import './GestionProducto.css';

const formatearFecha = (fecha) => {
    if (!fecha) return '';
    const s = String(fecha).substring(0, 10);
    const [year, month, day] = s.split('-');
    if (!day) return s;
    return `${day}/${month}/${year}`;
};

const formatearNumero = (valor, decimales = 0) => {
    if (valor == null) return '0';
    return Number(valor).toLocaleString('es-PY', { minimumFractionDigits: decimales, maximumFractionDigits: decimales });
};

function ChequesCartera() {
    // Catalogo clientes
    const [clientes, setClientes] = useState([]);

    // Selector de cliente
    const [clienteSeleccionado, setClienteSeleccionado] = useState(null);
    const [busquedaCliente, setBusquedaCliente] = useState('');
    const [dropdownClienteAbierto, setDropdownClienteAbierto] = useState(false);
    const dropdownClienteRef = useRef(null);

    // Filtro por vencimiento
    const [desdeVenc, setDesdeVenc] = useState('');
    const [hastaVenc, setHastaVenc] = useState('');

    // Datos
    const [cheques, setCheques] = useState([]);
    const [cargando, setCargando] = useState(false);
    const [busqueda, setBusqueda] = useState('');

    // Paginacion
    const [paginaActual, setPaginaActual] = useState(1);
    const [itemsPorPagina, setItemsPorPagina] = useState(20);

    useEffect(() => {
        axios.get(getApiUrl('/api/clientes'))
            .then(r => setClientes(r.data || []))
            .catch(e => console.error('Error clientes:', e));
    }, []);

    // Cargar todos al iniciar
    useEffect(() => {
        buscar();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Cerrar dropdown al click afuera
    useEffect(() => {
        const handler = (e) => {
            if (dropdownClienteRef.current && !dropdownClienteRef.current.contains(e.target)) setDropdownClienteAbierto(false);
        };
        document.addEventListener('mousedown', handler);
        return () => document.removeEventListener('mousedown', handler);
    }, []);

    const filtrarClientes = (b) => {
        const t = (b || '').toLowerCase();
        if (!t) return clientes.slice(0, 50);
        return clientes.filter(c =>
            String(c.idCliente || '').includes(t) ||
            String(c.nomCliente || '').toLowerCase().includes(t) ||
            String(c.rucCliente || '').toLowerCase().includes(t));
    };

    const seleccionarCliente = (c) => {
        setClienteSeleccionado(c);
        setBusquedaCliente('');
        setDropdownClienteAbierto(false);
    };

    const buscar = async () => {
        setCargando(true); setPaginaActual(1);
        try {
            const params = {};
            if (clienteSeleccionado) { params.desdeCli = clienteSeleccionado.idCliente; params.hastaCli = clienteSeleccionado.idCliente; }
            if (desdeVenc) params.desdeVenc = desdeVenc;
            if (hastaVenc) params.hastaVenc = hastaVenc;
            const r = await axios.get(getApiUrl('/api/cheques-cartera'), { params });
            setCheques(r.data || []);
        } catch (e) {
            console.error('Error cheques cartera:', e);
            alert('Error al buscar: ' + (e.response?.data || e.message));
            setCheques([]);
        } finally {
            setCargando(false);
        }
    };

    const chequesFiltrados = () => {
        const b = busqueda.trim().toLowerCase();
        if (!b) return cheques;
        return cheques.filter(c =>
            String(c.id_cliente || '').includes(b) ||
            String(c.nom_cliente || '').toLowerCase().includes(b) ||
            String(c.banco || '').toLowerCase().includes(b) ||
            String(c.id_banco || '').toLowerCase().includes(b) ||
            String(c.nrocheque || '').includes(b) ||
            String(c.nom_vendedor || '').toLowerCase().includes(b));
    };

    const filtrados = chequesFiltrados();
    const totMonto = filtrados.reduce((s, c) => s + (Number(c.monto) || 0), 0);

    const totalPaginas = Math.max(1, Math.ceil(filtrados.length / itemsPorPagina));
    const indicePrimero = (paginaActual - 1) * itemsPorPagina;
    const indiceUltimo = indicePrimero + itemsPorPagina;
    const actuales = filtrados.slice(indicePrimero, indiceUltimo);

    const imprimirPdf = () => {
        const params = new URLSearchParams();
        if (clienteSeleccionado) { params.set('desdeCli', clienteSeleccionado.idCliente); params.set('hastaCli', clienteSeleccionado.idCliente); }
        if (desdeVenc) params.set('desdeVenc', desdeVenc);
        if (hastaVenc) params.set('hastaVenc', hastaVenc);
        const qs = params.toString();
        window.open(getApiUrl('/api/cheques-cartera/imprimir') + (qs ? ('?' + qs) : ''), '_blank');
    };

    const exportarExcel = () => {
        const datos = filtrados.map(c => ({
            'Cod Cliente': c.id_cliente,
            'Cliente': c.nom_cliente,
            'Cod Banco': c.id_banco,
            'Banco': c.banco,
            'Nro Cheque': c.nrocheque,
            'Fecha': formatearFecha(c.fecha),
            'Vencimiento': formatearFecha(c.vencimiento),
            'Monto': Number(c.monto) || 0,
            'Vendedor': c.nom_vendedor
        }));
        const ws = XLSX.utils.json_to_sheet(datos);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, 'ChequesCartera');
        XLSX.writeFile(wb, 'cheques_en_cartera.xlsx');
    };

    return (
        <div className="gestion-producto-container">
            <div className="header-seccion">
                <h2>Cheques en Cartera por Cliente</h2>
                <div className="acciones-header">
                    <input type="text" className="input-busqueda" placeholder="Filtrar resultados..."
                        value={busqueda} onChange={(e) => { setBusqueda(e.target.value); setPaginaActual(1); }} />
                    <button className="btn btn-exportar" onClick={imprimirPdf}
                        style={{ background: '#6A1B9A' }} title="Listado de cheques en cartera (PDF)">📄 Imprimir PDF</button>
                    <button className="btn btn-exportar" onClick={exportarExcel} disabled={filtrados.length === 0}>Exportar Excel</button>
                </div>
            </div>

            {/* Filtros */}
            <div style={{ display: 'flex', alignItems: 'flex-end', gap: '15px', padding: '15px', backgroundColor: '#f5f5f5', borderRadius: '8px', marginBottom: '15px', flexWrap: 'wrap' }}>
                <div style={{ minWidth: '340px' }}>
                    <label style={{ display: 'block', fontSize: '13px', color: '#555', fontWeight: 500, marginBottom: '4px' }}>Cliente</label>
                    <div className="cuenta-selector" ref={dropdownClienteRef}>
                        {clienteSeleccionado ? (
                            <div className="cuenta-seleccionada">
                                <span>{clienteSeleccionado.idCliente} - {clienteSeleccionado.nomCliente}</span>
                                <button type="button" onClick={() => setClienteSeleccionado(null)} className="btn-limpiar">✕</button>
                            </div>
                        ) : (
                            <input type="text" placeholder="Buscar cliente... (vacío = todos)" value={busquedaCliente}
                                onChange={(e) => setBusquedaCliente(e.target.value)} onFocus={() => setDropdownClienteAbierto(true)}
                                className="form-input" />
                        )}
                        {dropdownClienteAbierto && !clienteSeleccionado && (
                            <div className="cuenta-dropdown">
                                {filtrarClientes(busquedaCliente).map((c, i) => (
                                    <div key={i} className="cuenta-item" onClick={() => seleccionarCliente(c)}>
                                        <strong>{c.idCliente}</strong> - {c.nomCliente} <span style={{ color: '#7f8c8d' }}>({c.rucCliente})</span>
                                    </div>
                                ))}
                                {filtrarClientes(busquedaCliente).length === 0 && (
                                    <div className="cuenta-item" style={{ color: '#7f8c8d' }}>Sin resultados</div>
                                )}
                            </div>
                        )}
                    </div>
                </div>
                <div>
                    <label style={{ display: 'block', fontSize: '13px', color: '#555', fontWeight: 500, marginBottom: '4px' }}>Vence desde</label>
                    <input type="date" value={desdeVenc} onChange={(e) => setDesdeVenc(e.target.value)} className="form-input" />
                </div>
                <div>
                    <label style={{ display: 'block', fontSize: '13px', color: '#555', fontWeight: 500, marginBottom: '4px' }}>Vence hasta</label>
                    <input type="date" value={hastaVenc} onChange={(e) => setHastaVenc(e.target.value)} className="form-input" />
                </div>
                <button className="btn btn-nuevo" onClick={buscar} disabled={cargando}>
                    {cargando ? 'Buscando...' : '🔍 Buscar'}
                </button>
            </div>

            {/* Grilla */}
            <div className="tabla-container">
                <table className="tabla-datos">
                    <thead>
                        <tr>
                            <th>Cod Cli</th>
                            <th>Cliente</th>
                            <th>Banco</th>
                            <th style={{ textAlign: 'right' }}>Nro Cheque</th>
                            <th>Fecha</th>
                            <th>Vencimiento</th>
                            <th style={{ textAlign: 'right' }}>Monto</th>
                            <th>Vendedor</th>
                        </tr>
                    </thead>
                    <tbody>
                        {cargando ? <tr><td colSpan="8" className="sin-datos">Cargando...</td></tr>
                        : actuales.length === 0 ? <tr><td colSpan="8" className="sin-datos">No hay cheques en cartera</td></tr>
                        : actuales.map((c, i) => (
                            <tr key={i}>
                                <td>{c.id_cliente}</td>
                                <td>{c.nom_cliente}</td>
                                <td>{c.id_banco} - {c.banco}</td>
                                <td style={{ textAlign: 'right' }}>{c.nrocheque}</td>
                                <td>{formatearFecha(c.fecha)}</td>
                                <td>{formatearFecha(c.vencimiento)}</td>
                                <td style={{ textAlign: 'right' }}>{formatearNumero(c.monto, 2)}</td>
                                <td>{c.nom_vendedor}</td>
                            </tr>
                        ))}
                    </tbody>
                    {filtrados.length > 0 && (
                        <tfoot>
                            <tr style={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>
                                <td colSpan="6" style={{ textAlign: 'right' }}>Total ({filtrados.length} cheques):</td>
                                <td style={{ textAlign: 'right' }}>{formatearNumero(totMonto, 2)}</td>
                                <td></td>
                            </tr>
                        </tfoot>
                    )}
                </table>
            </div>

            {/* Paginacion */}
            {filtrados.length > 0 && (
                <div className="paginacion-container">
                    <div className="paginacion-info">
                        <span>Mostrando {indicePrimero + 1} a {Math.min(indiceUltimo, filtrados.length)} de {filtrados.length}</span>
                        <select value={itemsPorPagina} onChange={(e) => { setItemsPorPagina(Number(e.target.value)); setPaginaActual(1); }} className="select-registros">
                            <option value={10}>10</option><option value={20}>20</option><option value={50}>50</option><option value={100}>100</option>
                        </select>
                    </div>
                    <div className="paginacion-controles">
                        <button onClick={() => setPaginaActual(1)} disabled={paginaActual === 1} className="btn-paginacion">|&lt;</button>
                        <button onClick={() => setPaginaActual(p => p - 1)} disabled={paginaActual === 1} className="btn-paginacion">&lt;</button>
                        <button onClick={() => setPaginaActual(p => p + 1)} disabled={paginaActual >= totalPaginas} className="btn-paginacion">&gt;</button>
                        <button onClick={() => setPaginaActual(totalPaginas)} disabled={paginaActual >= totalPaginas} className="btn-paginacion">&gt;|</button>
                    </div>
                </div>
            )}
        </div>
    );
}

export default ChequesCartera;
